// TraceFull v7 — API Keys & Security Settings
import { useState, useEffect } from 'react';
import { T, SectionHeader, Chip, Card, fmtDate } from '../components/ui';
import { apiKeysApi, auth, getToken, type ApiKey } from '../api/client';




const SCOPE_META: Record<string, { label: string; desc: string; color: string }> = {
  'products:read':    { label: 'Products Read',    desc: 'Read product catalog and scores',             color: T.sapphire },
  'products:write':   { label: 'Products Write',   desc: 'Create and update products',                  color: T.copper   },
  'alerts:read':      { label: 'Alerts Read',      desc: 'Read supply chain alerts',                    color: T.amber    },
  'alerts:write':     { label: 'Alerts Write',     desc: 'Acknowledge and resolve alerts',              color: T.amber    },
  'provenance:read':  { label: 'Provenance Read',  desc: 'Access provenance chain data',                color: T.sage     },
  'analytics:read':   { label: 'Analytics Read',   desc: 'Access analytics and reporting',              color: T.lavender },
  'suppliers:read':   { label: 'Suppliers Read',   desc: 'Read supplier and retailer data',             color: T.sub      },
  'webhooks:write':   { label: 'Webhooks',         desc: 'Manage webhook subscriptions',                color: T.teal     },
  'dpp:read':         { label: 'DPP Read',         desc: 'Access Digital Product Passports',            color: T.sapphire },
};


export default function ApiKeysPage() {
  const [keys, setKeys]           = useState<ApiKey[]>([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [newName, setNewName]     = useState('');
  const [newScopes, setNewScopes] = useState<string[]>(['products:read']);
  const [revealed, setRevealed]   = useState<string | null>(null);  // raw key shown once
  const [mfaData, setMfaData]     = useState<{ enabled: boolean; enrolled_at?: string } | null>(null);
  const [mfaSetup, setMfaSetup]   = useState<{ secret: string; qr_url: string } | null>(null);
  const [mfaToken, setMfaToken]   = useState('');
  const [mfaStep, setMfaStep]     = useState<'idle' | 'setup' | 'confirm' | 'done'>('idle');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [err, setErr]             = useState<string | null>(null);

  useEffect(() => {
    loadKeys();
    loadMfaStatus();
  }, []);

  async function loadKeys() {
    setLoading(true);
    try {
      const data = await apiKeysApi.list();
      setKeys(data.keys);
    } catch { setErr('Failed to load API keys'); }
    finally { setLoading(false); }
  }

  async function loadMfaStatus() {
    try {
      const me = await auth.me();
      setMfaData({ enabled: (me as any).mfa_enabled, enrolled_at: (me as any).mfa_enrolled_at });
    } catch {}
  }

  async function createKey() {
    if (!newName.trim()) return;
    setCreating(true); setErr(null);
    try {
      const res = await apiKeysApi.create({ name: newName.trim(), env: 'production', scopes: newScopes });
      setRevealed((res as any).key);
      await loadKeys();
      setNewName(''); setNewScopes(['products:read']);
    } catch (e: any) { setErr(e.message || 'Failed to create key'); }
    finally { setCreating(false); }
  }

  async function revokeKey(id: string) {
    if (!confirm('Revoke this key? All requests using it will immediately fail.')) return;
    try {
      await apiKeysApi.revoke(id);
      setKeys(prev => prev.filter(k => k.id !== id));
    } catch { setErr('Failed to revoke key'); }
  }

  async function beginMfaSetup() {
    setErr(null); setMfaStep('setup');
    try {
      const res = await fetch('/api/auth/mfa/setup', { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      setMfaSetup(data);
    } catch { setErr('Failed to start MFA setup'); setMfaStep('idle'); }
  }

  async function confirmMfa() {
    if (mfaToken.length !== 6) return;
    setErr(null);
    try {
      const res = await fetch('/api/auth/mfa/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ token: mfaToken }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.error); return; }
      setBackupCodes(data.backup_codes);
      setMfaStep('done');
      setMfaData({ enabled: true, enrolled_at: new Date().toISOString() });
    } catch { setErr('Verification failed'); }
  }

  const toggleScope = (scope: string) =>
    setNewScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);

  return (
    <div className="fade-up">
      <SectionHeader
        title="API Keys & Security"
        sub="Manage API access, scopes, and two-factor authentication"
      />

      {err && (
        <div style={{ background: T.crimson + '18', border: `1px solid ${T.crimson}40`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: T.crimson }}>{err}</div>
      )}

      {/* Revealed key banner — shown once only */}
      {revealed && (
        <div style={{ background: '#08180f', border: `1px solid ${T.sage}50`, borderRadius: 10, padding: '16px 20px', marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: T.sage, fontWeight: 700, marginBottom: 8 }}>🔑 New API Key — copy now, it won't be shown again</div>
          <div style={{ fontFamily: 'monospace', fontSize: 13, color: T.text, wordBreak: 'break-all', background: T.bg, padding: '10px 12px', borderRadius: 6, marginBottom: 10 }}>{revealed}</div>
          <button onClick={() => { navigator.clipboard.writeText(revealed); setRevealed(null); }}
            style={{ padding: '7px 16px', background: T.sage + '22', border: `1px solid ${T.sage}`, color: T.sage, borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Copy &amp; Dismiss
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 18 }}>

        {/* Left: API keys list */}
        <div>
          {/* Create form */}
          <Card style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Create New API Key</div>
            <input
              value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="Key name (e.g. Shopify Integration, Monitoring)"
              style={{ width: '100%', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
                padding: '9px 14px', color: T.text, fontSize: 13, outline: 'none',
                fontFamily: "'DM Sans',sans-serif", marginBottom: 14 }}
            />
            <div style={{ fontSize: 11, color: T.sub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>Scopes</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {Object.entries(SCOPE_META).map(([scope, meta]) => (
                <div key={scope} onClick={() => toggleScope(scope)} style={{
                  padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'all .1s',
                  background: newScopes.includes(scope) ? meta.color + '22' : T.bg,
                  border: `1px solid ${newScopes.includes(scope) ? meta.color : T.border}`,
                  color: newScopes.includes(scope) ? meta.color : T.sub,
                }}>{meta.label}</div>
              ))}
            </div>
            <button onClick={createKey} disabled={creating || !newName.trim()} style={{
              padding: '9px 20px', background: T.copper + '22', border: `1px solid ${T.copper}`,
              color: T.copper, borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: creating || !newName.trim() ? 'not-allowed' : 'pointer',
              opacity: creating || !newName.trim() ? 0.5 : 1,
            }}>{creating ? 'Creating…' : '+ Create API Key'}</button>
          </Card>

          {/* Keys list */}
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, fontSize: 13, fontWeight: 700, color: T.text }}>
              Active Keys ({keys.filter(k => k.status === 'active').length})
            </div>
            {loading ? (
              <div style={{ padding: 24, color: T.sub, fontSize: 12, textAlign: 'center' }}>Loading…</div>
            ) : keys.length === 0 ? (
              <div style={{ padding: 32, color: T.sub, fontSize: 12, textAlign: 'center' }}>No API keys yet — create one above</div>
            ) : (
              keys.map(k => (
                <div key={k.id} style={{ padding: '16px 18px', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 2 }}>{k.name}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, color: T.sub }}>{k.key_prefix}••••••••••••••••••••</div>
                    </div>
                    <Chip label={k.status === 'active' ? 'active' : 'revoked'} color={k.status === 'active' ? T.sage : T.crimson} />
                    {k.status === 'active' && (
                      <button onClick={() => revokeKey(k.id)} style={{
                        padding: '5px 12px', background: 'transparent', border: `1px solid ${T.crimson}50`,
                        color: T.crimson, borderRadius: 6, fontSize: 11, cursor: 'pointer',
                      }}>Revoke</button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    {k.scopes.map(s => <Chip key={s} label={SCOPE_META[s]?.label || s} color={SCOPE_META[s]?.color || T.sub} />)}
                  </div>
                  <div style={{ display: 'flex', gap: 18, fontSize: 11, color: T.dim }}>
                    <span>Created {fmtDate(k.created_at)}</span>
                    <span>Last used: {k.last_used_at ? fmtDate(k.last_used_at) : 'Never'}</span>
                    {k.call_count != null && <span>{k.call_count.toLocaleString()} total calls</span>}
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>

        {/* Right: MFA + account security */}
        <div>
          <Card style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Two-Factor Authentication</div>
            <div style={{ fontSize: 12, color: T.sub, marginBottom: 16, lineHeight: 1.5 }}>
              TOTP-based 2FA compatible with Google Authenticator, Authy, 1Password, and any RFC 6238 app.
            </div>

            {mfaData?.enabled ? (
              <div>
                <div style={{ background: T.sage + '15', border: `1px solid ${T.sage}40`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: T.sage, fontWeight: 700, marginBottom: 3 }}>✓ 2FA is active</div>
                  {mfaData.enrolled_at && <div style={{ fontSize: 11, color: T.sub }}>Enrolled {fmtDate(mfaData.enrolled_at)}</div>}
                </div>
                <button style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.border}`, color: T.sub, borderRadius: 7, fontSize: 12, cursor: 'pointer', width: '100%' }}>
                  Disable 2FA (requires current code)
                </button>
              </div>
            ) : mfaStep === 'idle' ? (
              <button onClick={beginMfaSetup} style={{
                width: '100%', padding: '10px', background: T.copper + '22',
                border: `1px solid ${T.copper}`, color: T.copper, borderRadius: 8,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>Enable Two-Factor Auth</button>
            ) : mfaStep === 'setup' && mfaSetup ? (
              <div>
                <div style={{ fontSize: 12, color: T.sub, marginBottom: 10 }}>Scan this QR code in your authenticator app:</div>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <img src={mfaSetup.qr_url} alt="TOTP QR" style={{ width: 180, height: 180, borderRadius: 8 }} />
                </div>
                <div style={{ fontSize: 11, color: T.sub, marginBottom: 6 }}>Or enter manually:</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: T.text, background: T.bg, padding: '8px 10px', borderRadius: 6, marginBottom: 14, wordBreak: 'break-all', letterSpacing: 2 }}>{mfaSetup.secret}</div>
                <input value={mfaToken} onChange={e => setMfaToken(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="Enter 6-digit code to confirm"
                  style={{ width: '100%', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 7, padding: '9px 12px', color: T.text, fontSize: 14, outline: 'none', textAlign: 'center', letterSpacing: 4, fontFamily: 'monospace', marginBottom: 10 }}
                />
                <button onClick={confirmMfa} disabled={mfaToken.length !== 6} style={{
                  width: '100%', padding: '9px', background: T.sage + '22', border: `1px solid ${T.sage}`,
                  color: T.sage, borderRadius: 7, fontSize: 13, fontWeight: 700,
                  cursor: mfaToken.length === 6 ? 'pointer' : 'not-allowed', opacity: mfaToken.length === 6 ? 1 : 0.4,
                }}>Verify & Enable 2FA</button>
              </div>
            ) : mfaStep === 'done' && backupCodes ? (
              <div>
                <div style={{ background: T.copper + '15', border: `1px solid ${T.copper}40`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: T.copper, fontWeight: 700, marginBottom: 6 }}>✓ 2FA enabled — save your backup codes</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    {backupCodes.map((c, i) => (
                      <div key={i} style={{ fontFamily: 'monospace', fontSize: 11, color: T.text, padding: '3px 0' }}>{c}</div>
                    ))}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: T.sub, lineHeight: 1.5 }}>Store these in a password manager. Each code can be used once if you lose your authenticator device.</div>
              </div>
            ) : null}
          </Card>

          <Card style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>API Reference</div>
            {[
              { method: 'GET',    path: '/api/products',           desc: 'List all products'        },
              { method: 'POST',   path: '/api/products',           desc: 'Create product'           },
              { method: 'GET',    path: '/api/alerts',             desc: 'List supply chain alerts' },
              { method: 'GET',    path: '/api/dpp/:sku',           desc: 'Get digital passport'     },
              { method: 'GET',    path: '/api/analytics/summary',  desc: 'Analytics overview'       },
              { method: 'POST',   path: '/api/products/import',    desc: 'Bulk import (CSV)'        },
            ].map(r => (
              <div key={r.path} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                <span style={{
                  padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                  minWidth: 36, textAlign: 'center',
                  background: r.method === 'GET' ? T.sapphire + '22' : T.copper + '22',
                  color: r.method === 'GET' ? T.sapphire : T.copper,
                }}>{r.method}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: T.text }}>{r.path}</div>
                  <div style={{ fontSize: 11, color: T.sub, marginTop: 2 }}>{r.desc}</div>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 12, fontSize: 11, color: T.sub, lineHeight: 1.5 }}>
              Authenticate with <code style={{ fontFamily: 'monospace', color: T.copper }}>X-API-Key: tf_••••</code> header.
              Full docs at <a href="#" style={{ color: T.sapphire }}>docs.tracefull.ai</a>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
